import type { PurchasingPurchaseLineRow } from '@/services/purchasingTypes'

export const EXTRACTION_MODEL_STRONG = 'gpt-5.6-terra'
export const STRONG_ENRICHMENT_ESTIMATED_COST = '~$0.05–$0.20 per document'

export type LineItemPurchaseMapInput = {
  id: string
  sort_order?: number | null
  description?: string | null
  amount: string | number
  vendor_item_code?: string | null
}

export type HighlightData = {
  amount: string | null
  description: string | null
  itemCode?: string | null
}

export function toLineItemHighlightData(
  item: Pick<LineItemPurchaseMapInput, 'amount' | 'description' | 'vendor_item_code'>,
  focus: 'row' | 'itemCode' = 'row',
): HighlightData {
  if (focus === 'itemCode' && item.vendor_item_code?.trim()) {
    return {
      amount: String(item.amount),
      description: item.description ?? null,
      itemCode: item.vendor_item_code.trim(),
    }
  }
  return {
    amount: String(item.amount),
    description: item.description ?? null,
  }
}

const ENRICHMENT_FLAGS = new Set([
  'enrichment_pending',
  'enrichment_needs_review',
  'enrichment_failed',
])

export function documentMayHavePurchaseLines(
  status: string,
  needsReviewFlags: string[] | undefined,
): boolean {
  if (status === 'approved') return true
  return (needsReviewFlags ?? []).some((flag) => ENRICHMENT_FLAGS.has(flag))
}

function parseAmount(value: string | number | null | undefined): number | null {
  if (value == null || value === '') return null
  const n = parseFloat(String(value))
  return Number.isFinite(n) ? n : null
}

function amountsMatch(
  lineAmount: string | number,
  purchaseExtended: string | null | undefined,
  tolerance = 0.02,
): boolean {
  const left = parseAmount(lineAmount)
  const right = parseAmount(purchaseExtended)
  if (left == null || right == null) return false
  return Math.abs(left - right) <= tolerance
}

function normalizeCode(code: string | null | undefined): string {
  return (code ?? '').trim().toLowerCase()
}

/**
 * Map purchase ledger rows onto document line items (line_item_id is not exposed by the API).
 */
export function mapPurchaseLinesToLineItems(
  lineItems: LineItemPurchaseMapInput[],
  purchaseLines: PurchasingPurchaseLineRow[],
): Map<string, PurchasingPurchaseLineRow> {
  const sortedItems = [...lineItems].sort(
    (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0),
  )
  const sortedPurchase = [...purchaseLines].sort((a, b) => a.id - b.id)
  const usedPurchase = new Set<number>()
  const result = new Map<string, PurchasingPurchaseLineRow>()

  for (const item of sortedItems) {
    const itemCode = normalizeCode(item.vendor_item_code)
    let best: PurchasingPurchaseLineRow | null = null
    let bestScore = -1

    for (const row of sortedPurchase) {
      if (usedPurchase.has(row.id)) continue
      let score = 0
      const rowCode = normalizeCode(row.vendor_item_code)
      if (itemCode && rowCode && itemCode === rowCode) score += 100
      if (amountsMatch(item.amount, row.extended_price)) score += 50
      const itemIdx = sortedItems.indexOf(item)
      const rowIdx = sortedPurchase.indexOf(row)
      if (itemIdx === rowIdx) score += 10
      if (score > bestScore) {
        bestScore = score
        best = row
      }
    }

    if (best != null && bestScore > 0) {
      usedPurchase.add(best.id)
      result.set(item.id, best)
    }
  }

  return result
}

const LINKED_CLASS = 'bg-emerald-50 text-emerald-800 ring-emerald-200'
const AMBER_CLASS = 'bg-amber-50 text-amber-800 ring-amber-200'
const IGNORED_CLASS = 'bg-gray-100 text-gray-700 ring-gray-200'

export type PurchaseLineProductBadge = {
  label: string
  className: string
}

export function purchaseLineProductBadge(
  line: PurchasingPurchaseLineRow,
): PurchaseLineProductBadge | null {
  if (line.review_status === 'excluded') {
    return { label: 'Excluded', className: IGNORED_CLASS }
  }
  if (line.product_id != null) {
    if (line.review_status === 'flagged') {
      return { label: 'Linked (review)', className: AMBER_CLASS }
    }
    return { label: 'Linked', className: LINKED_CLASS }
  }
  return { label: 'Not linked', className: AMBER_CLASS }
}

export function formatPurchaseDecimal(
  value: string | number | null | undefined,
  digits = 2,
): string {
  if (value == null || value === '') return '—'
  const n = parseFloat(String(value))
  if (!Number.isFinite(n)) return '—'
  return n.toFixed(digits)
}
